<template>
    <div class="tw:flex tw:flex-col">
        <div class="tw:flex tw:gap-2 tw:items-center tw:mb-4 tw:pl-4">
            <user-avatar v-if="user" :img-url="user.photo_url" :title="user.name" />
            <e-input :model-value="title" placeholder="Title" class="tw:flex-1 mr-4" @update:model-value="$emit('update:title', $event)" />
        </div>

        <div>
            <div class="column">
                <p>Message</p>
                <rich-text-editor
                    :model-value="message"
                    placeholder="Post a message to all students"
                    @update:model-value="$emit('update:message', $event)"
                />
            </div>

            <div class="column">
                <p>Attachments</p>
                <edverum-file-upload
                    :autosave="false"
                    :encode-as-base64="false"
                    :attachments="media"
                    :upload-context="uploadContext"
                    @add="$emit('add-attachment', $event)"
                />
            </div>

            <div class="tw:pl-4">
                <div class="field">
                    <e-switch
                        :model-value="sendEmailCopy"
                        type="is-success"
                        :disabled="notVisibleToStudents"
                        @update:model-value="$emit('update:sendEmailCopy', $event)"
                    >
                        {{ translate('notify_enrolled_students') }}
                    </e-switch>
                </div>
                <div class="field is-flex is-align-items-center">
                    <e-switch
                        :model-value="important"
                        type="is-success"
                        @update:model-value="$emit('update:important', $event)"
                    >
                        {{ translate('mark_as_important') }}
                    </e-switch>
                    <e-tooltip
                        label="This announcement will be marked as important and will stand out from the rest with a special icon."
                        position="is-right"
                    >
                        <e-icon
                            class="has-text-primary"
                            size="is-small"
                            style="padding-bottom: -7rem; display: flex"
                            icon="exclamation-circle"
                        />
                    </e-tooltip>
                </div>
                <div>
                    <div v-if="!editAnnouncementDate" class="tw:flex tw:gap-2">
                        <p>Schedule: {{ formattedScheduleDate }}</p>
                        <e-button
                            :disabled="!title || !message || submitting"
                            :loading="submitting"
                            @click="$emit('update:editAnnouncementDate', !editAnnouncementDate)"
                            @keyup="$emit('update:editAnnouncementDate', !editAnnouncementDate)"
                        >
                            <e-icon icon="pen" />
                        </e-button>
                    </div>

                    <div v-if="editAnnouncementDate">
                        <e-field>
                            <e-datetimepicker
                                :model-value="announcementDate"
                                :min-datetime="new Date()"
                                :max-datetime="maxDateTime"
                                :locale="$locale"
                                expanded
                                @update:model-value="$emit('update:announcementDate', $event)"
                            />

                            <div class="control">
                                <button
                                    class="button is-outlined"
                                    @click="$emit('update:editAnnouncementDate', false)"
                                    @keyup="$emit('update:editAnnouncementDate', false)"
                                >
                                    <e-icon icon="times-circle" />
                                </button>
                            </div>

                            <div class="control">
                                <e-button
                                    class="is-primary"
                                    label="Confirm"
                                    @click="$emit('update:editAnnouncementDate', false)"
                                    @keyup="$emit('update:editAnnouncementDate', false)"
                                />
                            </div>
                        </e-field>
                    </div>
                    <e-button
                        class="tw:mt-2! announcement-form__publish"
                        type="is-primary"
                        label="Publish"
                        :disabled="!title || !message || submitting"
                        @click="$emit('prompt-date-change')"
                        @keyup="$emit('prompt-date-change')"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n';

import Editor from '@/components/editor/Editor.vue';
import UserAvatar from '@/components/elements/Avatar.vue';
import EdverumFileUpload from '@/components/file-upload/EdverumFileUpload.vue';
import { FILE_UPLOAD_CONTEXT } from '@/constants/fileUploadRejection.js';

export default {
    name: 'AnnouncementForm',

    components: {
        UserAvatar,
        RichTextEditor: Editor,
        EdverumFileUpload,
    },

    props: {
        user: {
            type: Object,
            default: null,
        },

        title: {
            type: String,
            default: null,
        },

        message: {
            type: String,
            default: null,
        },

        sendEmailCopy: {
            type: Boolean,
            default: false,
        },

        important: {
            type: Boolean,
            default: false,
        },

        editAnnouncementDate: {
            type: Boolean,
            default: false,
        },

        announcementDate: {
            type: Date,
            default: null,
        },

        media: {
            type: Array,
            default: () => [],
        },

        submitting: {
            type: Boolean,
            default: false,
        },

        notVisibleToStudents: {
            type: Boolean,
            default: false,
        },

        maxDateTime: {
            type: Date,
            default: null,
        },
    },

    emits: [
        'update:title',
        'update:message',
        'update:sendEmailCopy',
        'update:important',
        'update:editAnnouncementDate',
        'update:announcementDate',
        'add-attachment',
        'prompt-date-change',
    ],

    setup() {
        const { t: translate } = useI18n();

        return { translate };
    },

    data() {
        return {
            uploadContext: FILE_UPLOAD_CONTEXT.ANNOUNCEMENT_ATTACHMENT_INSTRUCTOR,
        };
    },

    computed: {
        formattedScheduleDate() {
            if (!this.announcementDate) {
                return 'Immediately';
            }

            const now = new Date();
            const selectedDate = new Date(this.announcementDate);

            // Check if the selected date is within 1 minute of now (treat as immediate)
            const timeDiff = Math.abs(selectedDate.getTime() - now.getTime());
            const oneMinute = 60 * 1000;

            if (timeDiff < oneMinute) {
                return 'Immediately';
            }

            // Format the date for display using Intl
            const dateOptions = {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            };
            const timeOptions = {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            };

            const datePart = selectedDate.toLocaleDateString(this.$locale, dateOptions);
            const timePart = selectedDate.toLocaleTimeString(this.$locale, timeOptions);

            return `${datePart} at ${timePart}`;
        },
    },
};
</script>
